import _ from 'lodash';
import { Response } from 'miragejs';

// A new page only needs `collection` on the GET and `collection` plus `mutate` on the write.
// The id field defaults to "id". The id param defaults to the last :param in the URL.
// Operation names: create, read, update, remove, saveMany, removeMany.
// Older names still work: insert, patch, upsert, delete.
// Page-specific operations (roles, groups, resource tags, secrets) live in mutations/iam.js.

const SCOPE_TAGS = {
    projects: 'internal:bxp:projectId',
    folders: 'internal:bxp:folderId',
    organizations: 'internal:bxp:organizationId',
};

const collections = new Map();
const operations = {};
const insertHooks = { skipped: [], matched: [], creating: [], created: [] };
const updateHooks = [];
const removeHooks = [];
let idSequence = 0;

const readRequestBody = (request) => {
    if (!request?.requestBody) {
        return {};
    }
    if (typeof request.requestBody !== 'string') {
        return request.requestBody;
    }
    try {
        return JSON.parse(request.requestBody);
    } catch (_error) {
        return {};
    }
};

const readIds = (request, name) => {
    const raw = request?.queryParams?.[name];
    const values = Array.isArray(raw) ? raw : [raw];
    return values
        .flatMap((value) => String(value ?? '').split(','))
        .map((value) => value.trim())
        .filter(Boolean);
};

const collectionFor = (name) => {
    if (!collections.has(name)) {
        collections.set(name, { items: [], shape: 'items' });
    }
    return collections.get(name);
};

const takeId = (prefix) => {
    const id = `${prefix}-${Date.now()}-${idSequence}`;
    idSequence += 1;
    return id;
};

const urlParams = (url) => [...String(url || '').matchAll(/:([A-Za-z0-9_]+)/g)].map((match) => match[1]);

const registerMutation = (name, handler) => {
    operations[name] = handler;
};

const onInsert = (phase, handler) => {
    insertHooks[phase].push(handler);
};

const onUpdate = (handler) => {
    updateHooks.push(handler);
};

const onRemove = (handler) => {
    removeHooks.push(handler);
};

const applyStubDefaults = (stub) => {
    if (!stub) {
        return stub;
    }
    const params = urlParams(stub.url);
    const next = { ...stub, idField: stub.idField || 'id' };
    const associates = next.mutate === 'associate' || next.mutate === 'dissociate';
    if (!next.idParam && params.length && next.mutate && next.mutate !== 'associate') {
        next.idParam = params[params.length - 1];
    }
    if (associates && !next.scopeIdParam && params.length) {
        next.scopeIdParam = next.mutate === 'dissociate' && params.length > 1
            ? params[params.length - 2]
            : params[params.length - 1];
    }
    if (associates && !next.tagKey) {
        const segment = String(stub.url || '').split('/').find((part) => SCOPE_TAGS[part]);
        if (segment) {
            next.tagKey = SCOPE_TAGS[segment];
        }
    }
    return next;
};

const listBody = (collection, request, stub) => {
    const userId = stub?.assignmentParam && request?.params?.[stub.assignmentParam];
    const assignmentStore = stub?.assignmentCollection ? collectionFor(stub.assignmentCollection) : null;
    const tracked = Boolean(userId && assignmentStore?.tracked?.has(userId));
    const assigned = tracked
        ? assignmentStore.items.filter((item) => item.userId === userId)
        : null;
    const source = tracked
        ? assigned.map(({ userId: _userId, ...item }) => item)
        : collection.items;
    const listFilter = stub?.listFilter;
    const filterValue = listFilter && request?.queryParams?.[listFilter.query];
    const items = filterValue
        ? source.filter((item) => String(filterValue).split(',').includes(item?.[listFilter.field]))
        : source;
    if (collection.shape === 'array') {
        return _.cloneDeep(items);
    }
    const body = { items: _.cloneDeep(items) };
    if (collection.withCount) {
        body.count = items.length;
    }
    return body;
};

const applyFieldMap = (target, body, fieldMap, fillEmpty) => {
    Object.entries(fieldMap || {}).forEach(([targetPath, sourcePath]) => {
        const value = _.get(body, sourcePath);
        if (value !== undefined && (!fillEmpty || _.get(target, targetPath) === undefined)) {
            _.set(target, targetPath, value);
        }
    });
};

const applyDefaults = (target, defaults) => {
    Object.entries(defaults || {}).forEach(([path, value]) => {
        if (_.get(target, path) === undefined) {
            _.set(target, path, value);
        }
    });
};

/**
 * Copies each collection from its GET stub. A seeder owns the initial list when it names a
 * collection and does not declare a mutate operation. Called from seed() so a new simulator
 * session starts from the JSON file.
 *
 * An array stub is returned as an array (account credentials). An `{ items }` stub is returned
 * in that wrapper (notification config).
 */
const applyCollectionBody = (collectionName, body, withCount) => {
    const collection = collectionFor(collectionName);
    if (Array.isArray(body)) {
        collection.items = _.cloneDeep(body);
        collection.shape = 'array';
        collection.withCount = false;
        return;
    }
    collection.items = Array.isArray(body?.items) ? _.cloneDeep(body.items) : [];
    collection.shape = 'items';
    collection.withCount = Boolean(withCount);
};

const resetCollections = (seeders, stubResponses) => {
    collections.clear();
    idSequence = 0;
    for (const [key, stub] of Object.entries(seeders || {})) {
        if (stub?.collection && !stub.mutate) {
            applyCollectionBody(stub.collection, stubResponses?.[key], stub.withCount);
        }
        for (const [filterKey, filter] of Object.entries(stub?.filters || {})) {
            if (!filter.collection) {
                continue;
            }
            applyCollectionBody(filter.collection, stubResponses?.[`${key}::${filterKey}`], filter.withCount !== false);
        }
    }
};

const fillFromParams = (item, stub, request) => {
    Object.entries(stub.fillFromParams || {}).forEach(([field, param]) => {
        if (!item[field] && request?.params?.[param]) {
            item[field] = request.params[param];
        }
    });
};

const upsertItems = (stub, request) => {
    const collection = collectionFor(stub.collection);
    const idField = stub.idField || 'id';
    const incoming = _.get(readRequestBody(request), stub.itemsPath || 'items', []);
    const rows = Array.isArray(incoming) ? incoming : [incoming];
    const written = [];

    rows.forEach((raw) => {
        const item = _.cloneDeep(raw) || {};
        const currentId = item[idField];
        const isNew = !currentId || (stub.blankId && currentId === stub.blankId);

        if (isNew) {
            item[idField] = takeId(stub.collection);
            fillFromParams(item, stub, request);
            collection.items.push(item);
            written.push(_.cloneDeep(item));
            return;
        }

        const existingIndex = collection.items.findIndex((entry) => entry?.[idField] === currentId);
        if (existingIndex === -1) {
            fillFromParams(item, stub, request);
            collection.items.push(item);
            written.push(_.cloneDeep(item));
            return;
        }

        const existing = collection.items[existingIndex];
        const next = { ...existing, ...item };
        Object.keys(stub.fillFromParams || {}).forEach((field) => {
            if (!next[field] && existing[field]) {
                next[field] = existing[field];
            }
        });
        fillFromParams(next, stub, request);
        collection.items[existingIndex] = next;
        written.push(_.cloneDeep(next));
    });

    return { items: written };
};

const deleteItems = (stub, request) => {
    const collection = collectionFor(stub.collection);
    const idField = stub.idField || 'id';
    const ids = new Set(readIds(request, stub.idsQuery || 'ids'));
    collection.items = collection.items.filter((item) => !ids.has(item?.[idField]));
    return new Response(204);
};

const findByParam = (stub, request) => {
    const collection = collectionFor(stub.collection);
    const idField = stub.idField || 'id';
    const id = request?.params?.[stub.idParam];
    const index = collection.items.findIndex((item) => item?.[idField] === id);
    return { collection, idField, id, index };
};

const storeApi = {
    collectionFor,
    findByParam,
    readRequestBody,
    takeId,
};

const insertItem = (stub, request, template) => {
    const collection = collectionFor(stub.collection);
    const idField = stub.idField || 'id';
    const created = _.isPlainObject(template) ? _.cloneDeep(template) : {};
    created[idField] = takeId(stub.collection);
    const body = readRequestBody(request);
    applyFieldMap(created, body, stub.fieldMap);
    applyFieldMap(created, body, stub.fallbackFieldMap, true);
    applyDefaults(created, stub.defaults);
    if (stub.requireField && !created[stub.requireField]) {
        insertHooks.skipped.forEach((handler) => handler(stub, request, body, storeApi));
        return _.isPlainObject(template) ? _.cloneDeep(template) : {};
    }
    if (stub.matchField && created[stub.matchField]) {
        const existing = collection.items.find((item) => item?.[stub.matchField] === created[stub.matchField]);
        if (existing) {
            insertHooks.matched.forEach((handler) => handler(stub, request, existing, storeApi));
            return _.cloneDeep(existing);
        }
    }
    insertHooks.creating.forEach((handler) => handler(stub, request, created, storeApi));
    collection.items.push(created);
    insertHooks.created.forEach((handler) => handler(stub, request, created, storeApi));
    return _.cloneDeep(created);
};

const readItem = (stub, request, template) => {
    const { index, collection } = findByParam(stub, request);
    if (index === -1) {
        return template;
    }
    return _.cloneDeep(collection.items[index]);
};

const patchItem = (stub, request) => {
    const { collection, index } = findByParam(stub, request);
    if (index === -1) {
        return new Response(404, {}, { message: 'Not found' });
    }
    applyFieldMap(collection.items[index], readRequestBody(request), stub.fieldMap);
    updateHooks.forEach((handler) => handler(stub, request, collection.items[index], storeApi));
    return _.cloneDeep(collection.items[index]);
};

const removeItem = (stub, request) => {
    const { collection, index } = findByParam(stub, request);
    if (index === -1) {
        return new Response(404, {}, { message: 'Not found' });
    }
    const removed = collection.items[index];
    collection.items.splice(index, 1);
    removeHooks.forEach((handler) => handler(stub, request, removed, storeApi));
    return new Response(204);
};

const coreOperations = {
    create: insertItem,
    read: readItem,
    update: patchItem,
    remove: removeItem,
    saveMany: upsertItems,
    removeMany: deleteItems,
    insert: insertItem,
    patch: patchItem,
    upsert: upsertItems,
    delete: deleteItems,
};

Object.entries(coreOperations).forEach(([name, handler]) => {
    operations[name] = handler;
});

const GRAPHQL_MUTATIONS = new Set(['upsert', 'insert', 'create', 'patch', 'update', 'remove']);

const validateSeeders = (seeders, graphqlMutations) => {
    const routes = new Map();
    const warnings = [];
    Object.entries(seeders || {}).forEach(([key, stub]) => {
        if (!stub || stub.disabled === true || !stub.url || !stub.type) {
            return;
        }
        const routeKey = `${String(stub.type).toUpperCase()} ${stub.url}`;
        if (routes.has(routeKey)) {
            warnings.push(`${key} duplicates ${routes.get(routeKey)} on ${routeKey}. The later route wins.`);
        } else {
            routes.set(routeKey, key);
        }
        if (stub.mutate && !operations[stub.mutate]) {
            warnings.push(`${key} has unknown mutate "${stub.mutate}".`);
        }
        const params = new Set(urlParams(stub.url));
        ['idParam', 'scopeIdParam', 'roleIdParam', 'assignmentParam'].forEach((name) => {
            if (stub[name] && !params.has(stub[name])) {
                warnings.push(`${key} ${name} "${stub[name]}" is not a :param in ${stub.url}.`);
            }
        });
        const assignment = stub.assignment || {};
        ['scopeIdParam', 'roleIdParam'].forEach((name) => {
            if (assignment[name] && !params.has(assignment[name])) {
                warnings.push(`${key} assignment.${name} "${assignment[name]}" is not a :param in ${stub.url}.`);
            }
        });
    });
    Object.entries(graphqlMutations || {}).forEach(([key, spec]) => {
        if (spec?.mutate && !GRAPHQL_MUTATIONS.has(spec.mutate)) {
            warnings.push(`graphqlMutations.${key} has unknown mutate "${spec.mutate}".`);
        }
    });
    warnings.forEach((warning) => {
        console.warn(`[simulator] ${warning}`);
    });
    return warnings;
};

/**
 * Serves a config seeder from its in-memory collection.
 * Returns undefined when the seeder is not part of a collection, so the static stub is used.
 */
const handleCollectionRequest = (stub, request, template) => {
    const resolved = applyStubDefaults(stub);
    if (!resolved?.collection) {
        return undefined;
    }
    if (!resolved.mutate) {
        return listBody(collectionFor(resolved.collection), request, resolved);
    }
    const handler = operations[resolved.mutate];
    if (!handler) {
        console.warn(`[simulator] unknown mutate "${resolved.mutate}"`);
        return undefined;
    }
    return handler(resolved, request, template, storeApi);
};

export {
    resetCollections,
    handleCollectionRequest,
    registerMutation,
    onInsert,
    onUpdate,
    onRemove,
    applyStubDefaults,
    validateSeeders,
};
